import React, { useMemo } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { Box, Icon, Select, withStyles, FieldData } from 'vopay';
import { MultiselectOutsideOptions } from '@/components/UI';

import { PaymentFieldDefault } from './types';
import styles from './TransactionCreationFields.module.scss';

const classes = withStyles(styles);

export function useElinxBankAccountFields(
  formType: 'Pay' | 'Collect' | 'Connect' | 'Flex',
  country: string,
): FieldData<PaymentFieldDefault>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields' });
  const {
    EFTSendEnabled,
    EFTCollectEnabled,
    RTRWithdrawEnabled,
    ACHFundEnabled,
    ACHWithdrawEnabled,
    RTPWithdrawEnabled,
    FedwireWithdrawEnabled,
    FedwireFundEnabled,
  } = useLockedFeatures();

  type BankingPaymentRailOption = {
    label: React.ReactNode;
    value: string;
    isLocked: boolean;
    visible: boolean;
    isRecurringEnabled: boolean;
  };
  return useMemo(() => {
    const bankingPaymentRailOptions: BankingPaymentRailOption[] = [
      {
        label: t('paymentRail.eft'),
        value: 'eft',
        isLocked: formType === 'Pay' ? !EFTSendEnabled : !EFTCollectEnabled,
        visible: country === 'CA',
        isRecurringEnabled: true,
      },
      {
        label: t('paymentRail.vopayinstant'),
        value: 'vopayinstant',
        isLocked: !RTRWithdrawEnabled,
        visible: country === 'CA' && formType === 'Pay',
        isRecurringEnabled: false,
      },
      {
        label: t('paymentRail.ach'),
        value: 'ach',
        isLocked: formType === 'Pay' ? !ACHWithdrawEnabled : !ACHFundEnabled,
        visible: country === 'US',
        isRecurringEnabled: true,
      },
      {
        label: t('paymentRail.rtp'),
        value: 'rtp',
        isLocked: !RTPWithdrawEnabled,
        visible: country === 'US' && formType === 'Pay',
        isRecurringEnabled: false,
      },
      {
        label: t('paymentRail.fedwire'),
        value: 'fedwire',
        isLocked: formType === 'Pay' ? !FedwireWithdrawEnabled : !FedwireFundEnabled,
        visible: country === 'US',
        isRecurringEnabled: false,
      },
    ];

    const accountSelectionMethodOptions = [
      { label: t('accountSelectionMethodOptions.manual'), value: 'manual' },
      { label: t('accountSelectionMethodOptions.online'), value: 'online' },
    ];

    return [
      {
        label: () => t('Fields.bankingPaymentRail.label'),
        fieldName: 'BankingPaymentRail',
        getInputState: () => 'required',
        showField: (values) =>
          !!values.PaymentSelectionMethod?.includes('bank') &&
          values.ProvideOrRequestPaymentInformation === 'request' &&
          (values.Contact === 'new' || values.PaymentMethod === 'new' || values.PaymentMethod === 'request'),
        serializeField: (values) => values.BankingPaymentRail,
        renderField: ({ ctx, getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return (
            <Select
              version="2"
              placeholder={t('Fields.bankingPaymentRail.placeholder')}
              options={bankingPaymentRailOptions.filter(
                (option) => option.visible && (ctx.values.Frequency !== 'recurring' || option.isRecurringEnabled),
              )}
              sort={false}
              renderOption={(o, { Option, preventDefault }) => (
                <Option
                  onClick={() => {
                    if (o.isLocked) {
                      preventDefault();
                    }
                  }}
                >
                  <Box flx="row" ai="center" jc="sb" width="100%">
                    <div className={classes(o.isLocked && 'optionDisabled')}> {o.label}</div>
                    {o.isLocked && (
                      <Icon width="min-content" height="min-content" icon="lock2" size="sm" color="medium-light" />
                    )}
                  </Box>
                </Option>
              )}
              {...fieldData}
            />
          );
        },
        validate: (ctx) =>
          ctx.values.PaymentSelectionMethod?.includes('bank') && formType !== 'Connect'
            ? Yup.mixed().required()
            : Yup.mixed(),
      },
      {
        label: () => t('Fields.accountSelectionMethod.label'),
        fieldName: 'AccountSelectionMethod',
        getInputState: () => 'required',
        showField: (values) =>
          !!values.PaymentSelectionMethod?.includes('bank') &&
          values.ProvideOrRequestPaymentInformation === 'request' &&
          (values.Contact === 'new' || values.PaymentMethod === 'new' || values.PaymentMethod === 'request'),
        serializeField: (values) => {
          const accountValues = values.AccountSelectionMethod?.split(',');
          if (accountValues && accountValues.length > 1) {
            return 'any';
          }
          if (accountValues && accountValues.length === 1) {
            return accountValues[0];
          }
          return '';
        },
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string[], string>({
            formatValue: (value) => (value ? value.split(',') : []),
          });
          return (
            <MultiselectOutsideOptions
              placeholder={t('Fields.accountSelectionMethod.placeholder')}
              options={accountSelectionMethodOptions}
              {...fieldData}
            />
          );
        },

        validate: () => Yup.mixed().required(),
      },
    ];
  }, [formType, country]);
}
